import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * OAuth callback handler.
 *
 * Reads `#session_id=...` from the URL fragment, exchanges it with the
 * backend via POST /api/auth/google, then HARD-redirects to `/` so the
 * AuthProvider re-bootstraps cleanly from the freshly-set HttpOnly cookie.
 *
 * 5-second timeout fallback: if the exchange does not settle in time,
 * the loader is cleared and an error banner is shown with a manual
 * "Return to sign-in" recovery action.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
const HANDSHAKE_TIMEOUT_MS = 5000;

const AuthCallback = () => {
  const { exchangeGoogleSession } = useAuth();
  const ran = useRef(false);
  const [phase, setPhase] = useState("exchanging"); // 'exchanging' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    // Strip the fragment immediately so a reload doesn't replay the exchange.
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);

    if (!sessionId) {
      setErrorMsg("Missing session token in callback URL.");
      setPhase("error");
      return;
    }

    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setErrorMsg(
        "The handshake with the auth node timed out. Please retry sign-in."
      );
      setPhase("error");
    }, HANDSHAKE_TIMEOUT_MS);

    (async () => {
      try {
        await exchangeGoogleSession(sessionId);
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        // Hard-redirect so RoutedApp re-renders without the callback branch
        // and AuthProvider re-bootstraps from the now-set cookie.
        window.location.replace("/");
      } catch (err) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "We could not validate your Google session. Please retry sign-in.";
        setErrorMsg(detail);
        setPhase("error");
      }
    })();

    return () => window.clearTimeout(timeoutId);
  }, [exchangeGoogleSession]);

  if (phase === "error") {
    return (
      <div
        data-testid="auth-callback-error"
        className="min-h-[100dvh] w-full chaos-bg-gradient flex items-center justify-center px-5"
      >
        <div className="w-full max-w-md border border-[#FF0033]/40 bg-[#020617]/95 p-7 text-[#f1f5f9]">
          <div className="flex items-center gap-2.5 mb-4 font-mono text-[10px] uppercase tracking-[0.24em] text-[#FF0033]">
            <AlertTriangle size={14} />
            Handshake failed
          </div>
          <h2 className="text-xl font-extrabold uppercase tracking-tight mb-3">
            We hit a <span className="text-[#FF0033]">snag</span> verifying your
            session.
          </h2>
          <p
            data-testid="auth-callback-error-message"
            className="font-mono text-[12px] text-[#cbd5e1] leading-relaxed mb-6"
          >
            {errorMsg}
          </p>
          <button
            type="button"
            data-testid="auth-callback-retry-button"
            onClick={() => window.location.replace("/")}
            className="group w-full inline-flex items-center justify-center gap-2 bg-[#FF0033] hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.18em] px-5 py-3 text-xs border border-transparent hover:border-white transition-colors duration-200"
          >
            Return to sign-in
            <ArrowRight
              size={14}
              className="transition-transform group-hover:translate-x-1"
            />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="auth-callback"
      className="min-h-[100dvh] w-full flex flex-col items-center justify-center text-[#f1f5f9] chaos-bg-gradient"
    >
      <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-[#cbd5e1]">
        <span className="inline-block w-2 h-2 bg-[#FF0033] chaos-pulse" />
        Negotiating secure handshake…
      </div>
    </div>
  );
};

export default AuthCallback;
